import React, { useCallback, useEffect, useState, useReducer } from "react";
import { useHistory /*, useLocation*/ } from "react-router-dom";
import styled from "styled-components";

import {
	Button,
	InputAdornment,
	Chip,
	TextField,
	Autocomplete,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Snackbar,
	IconButton
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Search, Close } from "@mui/icons-material";
//import { useParams } from "react-router-dom";
import {
	usePromotions,
	usePromotionUtils
} from "../actions/promotions";
import { useBars, useBarUtils } from "../actions/bars";
import { firestore, firebase, auth } from "../services/firebase";
import { THEME_MAP } from "../theme";
import { BackDrop } from "../components/BackDrop";
import { useUserRoles } from "../globalstore";
import dayjs from "dayjs";

const PROMOTION_DURATION_OPTIONS = [
	{ value: "1", label: "1 Hour" },
	{ value: "2", label: "2 Hours" },
	{ value: "3", label: "3 Hours" },
	{ value: "4", label: "4 Hours" },
	{ value: "5", label: "5 Hours" },
	{ value: "6", label: "6 Hours" },
	{ value: "9", label: "9 Hours" },
	{ value: "12", label: "12 Hours" },
	{ value: "24", label: "24 Hours" },
	{ value: "continual", label: "Continual" }
];

const CONTINUAL_DURATION_FALLBACK_DAYS = 365;

export function Promotions(props) {
	const history = useHistory();
	const { getResults } = usePromotions();
	const { bars } = useBars();
	const { setBarsQuery } = useBarUtils();
	const [promotions, setPromotions] = useState([]);
	const [isBarPromotions, setIsBarPromotions] = useState(false);
	const [pagePromotions, setPagePromotions] = useState([]);
	const { clearSelectedPromotion, deletePromotion, savePromotion } =
		usePromotionUtils();
	const promotionsRef = firestore.collection("Promotions");
	const [showDeleteAlert, setShowDeleteAlert] = useState();
	const { userRoles: { isAdmin } } = useUserRoles();
	const { currentUser } = auth;

	const [selectedBar, setSelectedBar] = useState(null);
	const [barSearchTerm, setBarSearchTerm] = useState("");
	const [showBackDrop, setShowBackdrop] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	const [startDateTime, setStartDateTime] = useState(() =>
		dayjs()
			.second(0)
			.millisecond(0)
			.format("YYYY-MM-DDTHH:mm")
	);
	const [selectedDuration, setSelectedDuration] = useState("");
	const [computedEndDateTime, setComputedEndDateTime] = useState("");

	const [promotionState, promotionDispatch] = useReducer(
		promotionInfoReducer,
		emptyPromotionState,
		promotionInfoInit
	);
	const getAllPromotions = useCallback(
		async (barId) => {
			const promotions_ = await getResults(promotionsRef, barId);
			setPromotions(promotions_);
			setPagePromotions(promotions_);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[promotionsRef]
	);

	useEffect(() => {
		(async () => {
			const barId = props.match.params.id;
			if (barId && barId.length > 3) {
				setIsBarPromotions(true);
			}
			getAllPromotions(barId);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.match.params.id]);

	const navigateToAddPromotion = () => {
		clearSelectedPromotion();
		history.push("/promotion/new");
	};

	const handleSearch = (e) => {
		setPagePromotions(
			promotions.filter(
				(b) =>
					b.promotionName
						.toLowerCase()
						.indexOf(e.target.value.toLowerCase()) >= 0 ||
					b.bar?.barName
						?.toLowerCase()
						.indexOf(e.target.value.toLowerCase()) >= 0
			)
		);
	};

	const removePromotion = async () => {
		await deletePromotion(showDeleteAlert);
		setShowDeleteAlert("");
		getAllPromotions(props.match.params.id);
	};

	const dialogView = (
		<Dialog
			open={
				showDeleteAlert && showDeleteAlert.length > 0
					? true
					: false
			}
			onClose={() => setShowDeleteAlert("")}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">
				{"Are you sure?"}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					Deleting this promotion will result in the loss of any
					associated payment. Please consider reaching out to
					support@gobarhopper.com for support before confirming.
					Are you sure you want to proceed?
					<br />
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => setShowDeleteAlert("")}
					color="primary"
				>
					Cancel
				</Button>
				<Button
					onClick={() => removePromotion()}
					color="primary"
					autoFocus
				>
					Delete
				</Button>
			</DialogActions>
		</Dialog>
	);

	const openNote = (message, severity = "success") => {
		setSnackbar({ open: true, message, severity });
	};

	const closeNote = () => {
		setSnackbar((prev) => ({ ...prev, open: false }));
	};

	const handleInputChange = (event) => {
		promotionDispatch({
			type: "update",
			payload: {
				name: event.target.name,
				value: event.target.value
			}
		});
	};

	const handleSavePromotion = async (e) => {
		e.preventDefault();

		if (!promotionState.promotionName?.trim() || !startDateTime || !selectedDuration) {
			openNote("Promotion must include a name, start time, and duration.", "error");
			return;
		}

		if (!selectedBar?.id) {
			openNote("A bar must be selected before creating a promotion.", "error");
			return;
		}

		const startMoment = dayjs(startDateTime);
		if (!startMoment.isValid()) {
			openNote("Start time is invalid. Please pick a valid date and time.", "error");
			return;
		}

		const isContinual = selectedDuration === "continual";
		const parsedHours = isContinual ? null : parseInt(selectedDuration, 10);

		if (!isContinual && (Number.isNaN(parsedHours) || parsedHours <= 0)) {
			openNote("Duration must be one of the available hour options.", "error");
			return;
		}

		setShowBackdrop(true);

		try {
			const endMoment = isContinual
				? startMoment.add(CONTINUAL_DURATION_FALLBACK_DAYS, "day")
				: startMoment.add(parsedHours, "hour");

			const payload = {
				promotionName: promotionState.promotionName.trim(),
				promotionDescription: promotionState.promotionDescription?.trim() || "",
				promotionDurationHours: isContinual ? null : parsedHours,
				promotionIsContinual: isContinual,
				promotionStartingHours: firebase.firestore.Timestamp.fromDate(startMoment.toDate()),
				promotionEndingHours: firebase.firestore.Timestamp.fromDate(endMoment.toDate()),
				promotionDays: [startMoment.format("YYYY-MM-DD")],
				status: true,
				updatedAt: firebase.firestore.Timestamp.now(),
				createdBy: currentUser.uid,
				barId: selectedBar.id,
				userId: currentUser.uid,
				createdAt: firebase.firestore.Timestamp.now()
			};

			await savePromotion("new", payload);

			setShowBackdrop(false);
			openNote("Promotion created and activated.", "success");

			promotionDispatch({ type: "reset", payload: emptyPromotionState });
			setStartDateTime(dayjs().second(0).millisecond(0).format("YYYY-MM-DDTHH:mm"));
			setSelectedDuration("");
			setComputedEndDateTime("");
			setSelectedBar(null);
			setBarSearchTerm("");

			getAllPromotions(props.match.params.id);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error while saving promotion:", error);
			setShowBackdrop(false);
			openNote(`An error occurred: ${error.message}`, "error");
		}
	};

	useEffect(() => {
		if (!startDateTime || !selectedDuration) {
			return;
		}
		const startMoment = dayjs(startDateTime);
		if (!startMoment.isValid()) {
			return;
		}
		if (selectedDuration === "continual") {
			setComputedEndDateTime(
				startMoment
					.add(CONTINUAL_DURATION_FALLBACK_DAYS, "day")
					.second(0)
					.millisecond(0)
					.format("YYYY-MM-DDTHH:mm")
			);
			return;
		}
		const parsedHours = parseInt(selectedDuration, 10);
		if (Number.isNaN(parsedHours)) {
			return;
		}
		setComputedEndDateTime(
			startMoment
				.add(parsedHours, "hour")
				.second(0)
				.millisecond(0)
				.format("YYYY-MM-DDTHH:mm")
		);
	}, [startDateTime, selectedDuration]);

	return (
		<Container>
			{(isAdmin === "true" || isAdmin === true) && !isBarPromotions && (
				<AdminPromotionCreator onSubmit={handleSavePromotion}>
					<h2>Create Promotion (Admin)</h2>
					<Autocomplete
						options={bars}
						getOptionLabel={(option) => option.barName || ""}
						value={selectedBar}
						onChange={(event, newValue) => setSelectedBar(newValue)}
						inputValue={barSearchTerm}
						onInputChange={(event, newInputValue) => {
							setBarSearchTerm(newInputValue);
							setBarsQuery(newInputValue);
						}}
						loading={bars.length === 0 && barSearchTerm.length > 0}
						noOptionsText={barSearchTerm.length === 0 ? "Start typing to search bars..." : "No bars found"}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Select Bar"
								variant="outlined"
								placeholder="Search for a bar..."
								required
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{bars.length === 0 && barSearchTerm.length > 0 ? <CircularProgress color="inherit" size={20} /> : null}
											{params.InputProps.endAdornment}
										</>
									)
								}}
							/>
						)}
						fullWidth
					/>
					{selectedBar && (
						<>
							<TextField
								label="Promotion Name"
								name="promotionName"
								fullWidth
								value={promotionState.promotionName}
								onChange={handleInputChange}
								required
							/>
							<TextField
								label="Promotion Description"
								name="promotionDescription"
								fullWidth
								multiline
								value={promotionState.promotionDescription}
								onChange={handleInputChange}
							/>
							<TextField
								label="Start Date & Time"
								type="datetime-local"
								value={startDateTime}
								onChange={(event) => setStartDateTime(event.target.value)}
								InputLabelProps={{ shrink: true }}
								fullWidth
								required
							/>
							<FormControl fullWidth required>
								<InputLabel id="duration-select-label" shrink>
									Duration *
								</InputLabel>
								<Select
									displayEmpty
									labelId="duration-select-label"
									label="Duration *"
									value={selectedDuration}
									onChange={(event) => setSelectedDuration(event.target.value)}
								>
									{selectedDuration ? null : (
										<MenuItem value="">
											<em>-- Select Duration --</em>
										</MenuItem>
									)}
									{PROMOTION_DURATION_OPTIONS.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<TextField
								label={selectedDuration === "continual" ? "Review Date" : "Ends"}
								type="datetime-local"
								value={computedEndDateTime}
								InputLabelProps={{ shrink: true }}
								fullWidth
								disabled
								helperText={
									selectedDuration === "continual"
										? "Continual promotions stay active until you disable them."
										: "End time is calculated from the selected duration."
								}
							/>
							<div className="form-footer">
								<Button
									type="submit"
									color="primary"
									variant="contained"
									disabled={showBackDrop || !startDateTime || !selectedDuration}
								>
									Create & Activate
								</Button>
							</div>
						</>
					)}
					{showBackDrop && <BackDrop showBackDrop={showBackDrop} />}
				</AdminPromotionCreator>
			)}

			<div className="filters">
				<TextField
					className="input"
					placeholder="Filter"
					variant="outlined"
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search>Filled</Search>
							</InputAdornment>
						)
					}}
					onChange={handleSearch}
				/>
				{isBarPromotions && (
					<Button
						color="primary"
						variant="contained"
						onClick={navigateToAddPromotion}
					>
						Create Promotion
					</Button>
				)}
			</div>
			<div className="promotion-container">
				{pagePromotions?.map((snapshot) => (
					<PromotionListItem
						key={snapshot.id}
						onDelete={(id) => setShowDeleteAlert(id)}
						promotionSnapshot={snapshot}
					/>
				))}
				{pagePromotions.length === 0 && (
					<NoResultsContainer>
						<span className="promotionName">
							No promotions created
						</span>
					</NoResultsContainer>
				)}
			</div>

			{dialogView}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={closeNote}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert severity={snackbar.severity} onClose={closeNote}>
					<IconButton
						size="small"
						aria-label="close"
						color="inherit"
						onClick={closeNote}
					>
						<Close fontSize="small" />
					</IconButton>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
}

const emptyPromotionState = {
	promotionName: "",
	promotionCoverImage: "",
	promotionImages: [],
	promotionDescription: "",
	promotionDays: "",
	promotionEndingHours: "",
	promotionStartingHours: "",
	promotionPlanId: "",
	promotionDurationHours: null,
	promotionIsContinual: false,
	hasChanged: false,
	status: false
};

function promotionInfoInit(promotionInfo) {
	return {
		original: promotionInfo,
		...promotionInfo,
		hasChanged: false
	};
}

function promotionInfoReducer(state, action) {
	const { type, payload } = action;
	let hasChanged = false;
	switch (type) {
		case "reset":
			return promotionInfoInit(payload || state.original);
		case "update":
			hasChanged = true;
			return {
				...state,
				[payload.name]: payload.value,
				hasChanged
			};
		default:
			return state;
	}
}
const Container = styled.div`
	height: 100%;
	width: 100%;

	overflow: auto;

	padding: 24px;

	.input {
		background: #fff;
	}

	.filters {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	.promotion-container {
		width: 100%;
		min-height: 100px;
		border-radius: 4px;
		border: 1px solid ${THEME_MAP["color-basic-400"]};
		background: ${THEME_MAP["color-basic-100"]};

		padding: 8px 0;

		margin-bottom: 16px;

		max-height: calc(100% - 125px);
		overflow-y: scroll;
	}
`;

const AdminPromotionCreator = styled.form`
	max-width: 900px;
	margin: 0 auto 32px;
	background: ${THEME_MAP["color-basic-100"]};
	border-radius: 8px;
	border: 2px solid ${THEME_MAP["color-primary-400"]};
	padding: 24px;
	gap: 16px;

	display: flex;
	flex-direction: column;

	h2 {
		margin: 0 0 8px;
		color: ${THEME_MAP["color-primary-600"]};
	}

	.form-footer {
		width: 100%;
		display: flex;
		justify-content: flex-end;
		margin-top: 8px;
	}
`;

function PromotionListItem({
	promotionSnapshot,
	//barSnapshot,
	onDelete
}) {
	const [currentRef] = useState(promotionSnapshot.ref);
	//const [bar, setBar] = useState(barSnapshot?.data());
	const [promotion, setPromo] = useState(promotionSnapshot);
	const history = useHistory();
	//const { id: promotionId } = useParams();
	const { setSelectedPromotion } = usePromotionUtils();

	useEffect(() => {
		if (currentRef) {
			return currentRef.onSnapshot((snap) => {
				setPromo({ ...snap.data(), id: snap.id });
				//setBar(snap.data());
			});
		}
	}, [currentRef]);

	const handleClick = () => {
		setSelectedPromotion(promotionSnapshot);
		history.push(`/promotion/${promotionSnapshot.id}`);
	};

	if (
		!promotion.promotionName ||
		!promotion.bar ||
		!promotion.bar.barName
	) {
		return <></>;
	}
	const now = dayjs();
	const promoIsActive = dayjs(
		new Date(
			promotion.promotionEndingHours.seconds * 1000 +
				promotion.promotionEndingHours.nanoseconds / 1000000
		)
	).isAfter(now);

	return (
		<>
			<PromoItem>
				<div
					style={{
						display: "inline-block",
						width: "calc(100% - 90px)"
					}}
					onClick={handleClick}
				>
					<span className="promotionName">
						{promotion?.promotionName || "Promotion Name"}
					</span>
					<span className="barName">
						{promotion?.bar?.barName || "Bar Name"}
					</span>

					{promoIsActive && (
						<Chip
							style={{
								fontSize: "12px",
								width: "auto",
								height: "auto",
								background: "#C42D3E",
								color: "white",
								marginLeft: "10px"
							}}
							label={"Active"}
							variant="outlined"
						/>
					)}
				</div>
				<Button
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						onDelete(promotionSnapshot.id);
					}}
					style={{ float: "right" }}
					aria-label="remove permissions"
					variant="outlined"
				>
					DELETE
				</Button>
			</PromoItem>
		</>
	);
}

const PromoItem = styled.div`
	padding: 8px 24px;
	width: 100%;
	justify-content: space-between;
	margin: 4px 0;

	&:hover {
		background: ${THEME_MAP["color-primary-transparent-100"]};
		cursor: pointer;
	}

	span {
		display: block;

		&.promotionName {
			color: ${THEME_MAP["color-primary-700"]};
			font-size: 1.2rem;
			/* font-weight: bold; */
		}

		&.barName {
			color: ${THEME_MAP["color-basic-600"]};
		}
	}
`;

const NoResultsContainer = styled.div`
	padding: 8px 24px;
`;
